import { forwardRef, useEffect, useRef, useState } from "react";
import { Popover, Whisper } from "rsuite";
import IconButton from "./IconButton";

interface AppColorPickerProps {
  title?: string;
  containerCSS?: string;
  name?: string;
  value: string;
  onChange: (e: any) => any;
  titleCSS?: string;
  inputStyles?: object;
  theme?: string[] | readonly string[];
  onClear?: () => void;
  disabled?: boolean;
}

const AppColorPicker = ({
  title,
  containerCSS = "",
  name = "Color",
  value,
  onChange,
  titleCSS = "",
  inputStyles = {},
  theme,
  onClear,
  disabled = false,
}: AppColorPickerProps) => {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const whisperRef = useRef<any>();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const ColorPickerInput = (
    <input
      style={{
        ...inputStyles,
        ...(theme ? { opacity: 0, width: 1, height: 1 } : {}),
      }}
      type="color"
      name={name}
      value={localValue || ""}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={(e) => onChange(e.target.value)}
      ref={inputRef}
      tabIndex={0}
      disabled={!!disabled}
    />
  );

  const ClearInput =
    onClear && value ? (
      <IconButton
        icon="close"
        color="danger"
        onClick={onClear}
        className="ml-2"
      />
    ) : null;

  return (
    <div className={`d-flex align-items-center ${containerCSS}`}>
      {title && (
        <h5 className={`mb-0 mr-3 ${titleCSS ? titleCSS : ""}`}>{title}:</h5>
      )}
      {!theme && ColorPickerInput}
      {!theme && ClearInput}
      {theme && (
        <Whisper
          placement="autoVertical"
          ref={whisperRef}
          speaker={
            <Popover
              className="sidebar-popover px-3 py-2"
              style={{ maxWidth: 350 }}
            >
              <div className="d-flex align-items-center" style={{ height: 50 }}>
                <h5 className="mb-0 pr-2">Selected Color: </h5>
                {value ? (
                  <ColorSquare
                    color={localValue}
                    selected={false}
                    onClick={() => {}}
                  />
                ) : (
                  "None. Theme colors will be used."
                )}
                {ClearInput}
              </div>
              <div className="d-flex flex-wrap pb-2" style={{ gap: 3 }}>
                {theme.map((color) => (
                  <ColorSquare
                    key={color}
                    color={color}
                    onClick={() => {
                      setLocalValue(color);
                      onChange(color);
                    }}
                    selected={color === value}
                  />
                ))}
                <ColorSquare
                  color={undefined}
                  onClick={() => {
                    inputRef.current?.click();
                    inputRef.current?.focus();
                  }}
                  input={ColorPickerInput}
                />
              </div>
            </Popover>
          }
        >
          <ColorSquare
            color={value}
            onClick={() => whisperRef.current?.open()}
          />
        </Whisper>
      )}
    </div>
  );
};

const ColorSquare = forwardRef(
  (
    {
      color,
      onClick,
      selected,
      input,
    }: {
      color: string | undefined | null;
      onClick: () => void;
      selected?: boolean;
      input?: any;
    },
    ref: any
  ) => {
    const Color = (
      <div
        style={{
          backgroundColor: color || "#ffffff",
          width: 25,
          height: 25,
          border: selected ? "2px solid black" : "1px solid var(--medium)",
        }}
        className="rounded cursor-pointer"
        onClick={onClick}
        ref={ref}
      ></div>
    );

    const NoColor = (
      <>
        <div
          onClick={onClick}
          className="rounded cursor-pointer"
          style={{
            width: 25,
            height: 25,
            backgroundImage: `linear-gradient(45deg, #ccc 25%, transparent 25%), 
      linear-gradient(135deg, #ccc 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #ccc 75%),
      linear-gradient(135deg, transparent 75%, #ccc 75%)`,
            backgroundSize: "10px 10px",
            backgroundPosition: "0 0, 5px 0, 5px -5px, 0px 5px",
            border: "1px solid var(--medium)",
          }}
          ref={ref}
        ></div>
        {input && input}
      </>
    );

    return color ? Color : NoColor;
  }
);

export default AppColorPicker;
